import React, { useState, useEffect } from 'react';
import {
  useHourlyDistributionsByDay,
  useDistributionsSummaryByDay,
  useDistributionAlertByDay,
  useCreateHourlyDistribution,
  useUpdateHourlyDistribution,
  useDeleteHourlyDistribution,
  useDeleteHourlyDistributionsByDay,
  useDistributeUniformly,
  useCopyDistributions,
  useResetAllHourlyDistributions,
  type HourlyDistribution,
  type DayOfWeek,
  dayOfWeekLabels,
  dayOfWeekShortLabels,
  dayOfWeekOrder,
  formatTime,
  getCurrentDayOfWeek,
} from '../hooks/useHourlyDistribution';

// SVG Icons
const ChartIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" />
  </svg>
);

const CalendarIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const PlusIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const ZapIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </svg>
);

const CopyIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

const TrashIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

const EditIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const CheckIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const PauseIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="6" y="4" width="4" height="16" /><rect x="14" y="4" width="4" height="16" />
  </svg>
);

const AlertIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const InfoIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);

const SaveIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" /><polyline points="17 21 17 13 7 13 7 21" /><polyline points="7 3 7 8 15 8" />
  </svg>
);

const CloseIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const ClockIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
  </svg>
);

const InboxIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" /><path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
  </svg>
);


export const HourlyDistributionPanel: React.FC = () => {
  const [selectedDay, setSelectedDay] = useState<DayOfWeek>(getCurrentDayOfWeek());
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingDistribution, setEditingDistribution] = useState<HourlyDistribution | null>(null);
  const [showDistributeModal, setShowDistributeModal] = useState(false);
  const [showCopyModal, setShowCopyModal] = useState(false);

  const [distributeConfig, setDistributeConfig] = useState({
    businessStartHour: 7,
    businessEndHour: 22,
    clearExisting: true,
  });

  const [copyConfig, setCopyConfig] = useState({
    toDays: [] as DayOfWeek[],
    overwrite: false,
  });

  const { summary, loading: loadingSummary, refetch: refetchSummary } = useDistributionsSummaryByDay();
  const { distributions, loading: loadingDistributions, refetch: refetchDistributions } = useHourlyDistributionsByDay(selectedDay);
  const { alert, refetch: refetchAlert } = useDistributionAlertByDay(selectedDay);

  const { create, creating } = useCreateHourlyDistribution();
  const { update, updating } = useUpdateHourlyDistribution();
  const { deleteDistribution, deleting } = useDeleteHourlyDistribution();
  const { deleteByDay, deleting: deletingByDay } = useDeleteHourlyDistributionsByDay();
  const { distribute, distributing } = useDistributeUniformly();
  const { copy, copying } = useCopyDistributions();
  const { reset, resetting } = useResetAllHourlyDistributions();

  const [formData, setFormData] = useState({
    startHour: 12,
    startMinute: 0,
    endHour: 14,
    endMinute: 0,
    percentage: 20,
    description: '',
  });

  useEffect(() => {
    if (editingDistribution) {
      setFormData({
        startHour: editingDistribution.startHour,
        startMinute: editingDistribution.startMinute,
        endHour: editingDistribution.endHour,
        endMinute: editingDistribution.endMinute,
        percentage: editingDistribution.percentage,
        description: editingDistribution.description || '',
      });
    }
  }, [editingDistribution]);

  const refetchAll = async () => {
    await Promise.all([refetchSummary(), refetchDistributions(), refetchAlert()]);
  };

  const handleCreate = async () => {
    try {
      await create({ dayOfWeek: selectedDay, ...formData });
      setShowCreateModal(false);
      setFormData({ startHour: 12, startMinute: 0, endHour: 14, endMinute: 0, percentage: 20, description: '' });
      await refetchAll();
      window.alert('Distribución horaria creada exitosamente');
    } catch (error: any) {
      window.alert(error.message || 'Error al crear la distribución horaria');
    }
  };

  const handleUpdate = async () => {
    if (!editingDistribution) return;
    try {
      await update({ id: editingDistribution.id, ...formData });
      setEditingDistribution(null);
      setFormData({ startHour: 12, startMinute: 0, endHour: 14, endMinute: 0, percentage: 20, description: '' });
      await refetchAll();
      window.alert('Distribución horaria actualizada exitosamente');
    } catch (error: any) {
      window.alert(error.message || 'Error al actualizar la distribución horaria');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar esta distribución?')) return;
    try {
      await deleteDistribution(id);
      await refetchAll();
      window.alert('Distribución eliminada exitosamente');
    } catch (error: any) {
      window.alert(error.message || 'Error al eliminar la distribución');
    }
  };

  const handleDeleteByDay = async () => {
    if (!window.confirm(`¿Estás seguro de que deseas eliminar TODAS las distribuciones de ${dayOfWeekLabels[selectedDay]}?`)) return;
    try {
      const count = await deleteByDay(selectedDay);
      await refetchAll();
      window.alert(`Se eliminaron ${count} distribuciones de ${dayOfWeekLabels[selectedDay]}`);
    } catch (error: any) {
      window.alert(error.message || 'Error al eliminar las distribuciones');
    }
  };

  const handleToggleActive = async (distribution: HourlyDistribution) => {
    try {
      await update({ id: distribution.id, isActive: !distribution.isActive });
      await refetchAll();
    } catch (error: any) {
      window.alert(error.message || 'Error al cambiar el estado');
    }
  };

  const handleDistributeUniformly = async () => {
    try {
      const result = await distribute({
        dayOfWeek: selectedDay,
        businessStartHour: distributeConfig.businessStartHour,
        businessEndHour: distributeConfig.businessEndHour,
        clearExisting: distributeConfig.clearExisting,
      });
      await refetchAll();
      setShowDistributeModal(false);
      window.alert(`Se crearon ${result.totalCreated} distribuciones para ${dayOfWeekLabels[selectedDay]}`);
    } catch (error: any) {
      window.alert(error.message || 'Error al distribuir uniformemente');
    }
  };

  const handleCopyDistributions = async () => {
    if (copyConfig.toDays.length === 0) {
      window.alert('Selecciona al menos un día de destino');
      return;
    }
    try {
      const result = await copy({ fromDay: selectedDay, toDays: copyConfig.toDays, overwrite: copyConfig.overwrite });
      await refetchAll();
      setShowCopyModal(false);
      setCopyConfig({ toDays: [], overwrite: false });
      window.alert(`Distribuciones copiadas a ${result.copiedTo.length} días (${result.totalCopied} en total)`);
    } catch (error: any) {
      window.alert(error.message || 'Error al copiar las distribuciones');
    }
  };

  const handleResetAll = async () => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar TODAS las distribuciones de TODOS los días?')) return;
    try {
      await reset();
      await refetchAll();
      window.alert('Todas las distribuciones han sido eliminadas');
    } catch (error: any) {
      window.alert(error.message || 'Error al resetear las distribuciones');
    }
  };

  const closeModal = () => {
    setShowCreateModal(false);
    setEditingDistribution(null);
    setFormData({ startHour: 12, startMinute: 0, endHour: 14, endMinute: 0, percentage: 20, description: '' });
  };

  const toggleCopyDay = (day: DayOfWeek) => {
    if (day === selectedDay) return;
    setCopyConfig(prev => ({
      ...prev,
      toDays: prev.toDays.includes(day) ? prev.toDays.filter(d => d !== day) : [...prev.toDays, day],
    }));
  };

  const selectAllDaysForCopy = () => {
    setCopyConfig(prev => ({ ...prev, toDays: dayOfWeekOrder.filter(d => d !== selectedDay) }));
  };

  const clearCopySelection = () => {
    setCopyConfig(prev => ({ ...prev, toDays: [] }));
  };

  const currentDaySummary = summary.find(s => s.dayOfWeek === selectedDay);

  if (loadingSummary) {
    return (
      <div className="hdp-panel">
        <div className="hdp-loading">
          <div className="hdp-spinner"></div>
          <span>Cargando distribuciones...</span>
        </div>
        <style>{styles}</style>
      </div>
    );
  }

  return (
    <div className="hdp-panel">
      {/* Header */}
      <div className="hdp-header">
        <div className="hdp-header-title">
          <ChartIcon />
          <span>Distribución de Porcentajes por Hora</span>
        </div>
        <button className="hdp-btn hdp-btn-danger hdp-btn-sm" onClick={handleResetAll} disabled={resetting}>
          <TrashIcon />
          <span>{resetting ? '...' : 'Resetear'}</span>
        </button>
      </div>

      {/* Days Summary Bar */}
      <div className="hdp-days-bar">
        {dayOfWeekOrder.map((day) => {
          const daySummary = summary.find(s => s.dayOfWeek === day);
          const isSelected = day === selectedDay;
          const isComplete = daySummary?.isComplete || false;
          const percentage = daySummary?.totalPercentage || 0;

          return (
            <button
              key={day}
              className={`hdp-day-tab ${isSelected ? 'selected' : ''} ${isComplete ? 'complete' : ''}`}
              onClick={() => setSelectedDay(day)}
            >
              <span className="hdp-day-name">{dayOfWeekShortLabels[day]}</span>
              <span className="hdp-day-pct">{percentage.toFixed(0)}%</span>
              <span className={`hdp-day-dot ${isComplete ? 'complete' : ''}`}></span>
            </button>
          );
        })}
      </div>

      {/* Selected Day Header */}
      <div className="hdp-day-header">
        <div className="hdp-day-info">
          <h3><CalendarIcon /> {dayOfWeekLabels[selectedDay]}</h3>
          <div className="hdp-day-stats">
            <span className={currentDaySummary?.isComplete ? 'complete' : 'pending'}>
              {currentDaySummary?.totalPercentage.toFixed(1) || 0}% asignado
            </span>
            <span>{currentDaySummary?.activeDistributions || 0} activas</span>
          </div>
        </div>
        <div className="hdp-day-actions">
          <button className="hdp-btn hdp-btn-primary" onClick={() => setShowCreateModal(true)}>
            <PlusIcon /> Nueva
          </button>
          <button className="hdp-btn hdp-btn-secondary" onClick={() => setShowDistributeModal(true)}>
            <ZapIcon /> Uniforme
          </button>
          <button className="hdp-btn hdp-btn-secondary" onClick={() => setShowCopyModal(true)} disabled={distributions.length === 0}>
            <CopyIcon /> Copiar
          </button>
          {distributions.length > 0 && (
            <button className="hdp-btn hdp-btn-danger hdp-btn-icon" onClick={handleDeleteByDay} disabled={deletingByDay}>
              <TrashIcon />
            </button>
          )}
        </div>
      </div>

      {/* Alert */}
      {alert && alert.hasAlert && (
        <div className="hdp-alert">
          <AlertIcon />
          <div>
            <strong>Porcentaje sin asignar</strong>
            <p>{alert.totalAssigned.toFixed(1)}% asignado - {alert.unassignedPercentage.toFixed(1)}% restante</p>
          </div>
        </div>
      )}

      {/* Progress Bar */}
      <div className="hdp-progress-section">
        <div className="hdp-progress-header">
          <span>Progreso</span>
          <span className={currentDaySummary?.isComplete ? 'complete' : ''}>
            {currentDaySummary?.totalPercentage.toFixed(1) || 0}% / 100%
          </span>
        </div>
        <div className="hdp-progress-bar">
          <div
            className={`hdp-progress-fill ${(currentDaySummary?.totalPercentage || 0) === 100 ? 'complete' : (currentDaySummary?.totalPercentage || 0) > 100 ? 'over' : ''}`}
            style={{ width: `${Math.min(currentDaySummary?.totalPercentage || 0, 100)}%` }}
          />
        </div>
      </div>

      {/* Distributions Table */}
      <div className="hdp-table-section">
        {loadingDistributions ? (
          <div className="hdp-loading-inline">
            <div className="hdp-spinner-sm"></div>
            <span>Cargando...</span>
          </div>
        ) : distributions.length === 0 ? (
          <div className="hdp-empty">
            <InboxIcon />
            <p>Sin distribuciones para {dayOfWeekLabels[selectedDay]}</p>
            <div className="hdp-empty-actions">
              <button className="hdp-btn hdp-btn-primary" onClick={() => setShowCreateModal(true)}>
                <PlusIcon /> Crear
              </button>
              <button className="hdp-btn hdp-btn-secondary" onClick={() => setShowDistributeModal(true)}>
                <ZapIcon /> Uniforme
              </button>
            </div>
          </div>
        ) : (
          <table className="hdp-table">
            <thead>
              <tr>
                <th>Horario</th>
                <th>%</th>
                <th>Descripción</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {distributions.map((dist) => (
                <tr key={dist.id} className={!dist.isActive ? 'inactive' : ''}>
                  <td className="hdp-time-cell">
                    {formatTime(dist.startHour, dist.startMinute)} - {formatTime(dist.endHour, dist.endMinute)}
                  </td>
                  <td><span className="hdp-pct-badge">{dist.percentage.toFixed(1)}%</span></td>
                  <td>{dist.description || <span className="hdp-muted">-</span>}</td>
                  <td>
                    <button className={`hdp-status-btn ${dist.isActive ? 'active' : ''}`} onClick={() => handleToggleActive(dist)} disabled={updating}>
                      {dist.isActive ? <><CheckIcon /> Activa</> : <><PauseIcon /> Inactiva</>}
                    </button>
                  </td>
                  <td>
                    <div className="hdp-action-btns">
                      <button className="hdp-btn hdp-btn-sm hdp-btn-secondary" onClick={() => setEditingDistribution(dist)}><EditIcon /></button>
                      <button className="hdp-btn hdp-btn-sm hdp-btn-danger" onClick={() => handleDelete(dist.id)} disabled={deleting}><TrashIcon /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Weekly Overview */}
      <div className="hdp-weekly">
        <h4><ChartIcon /> Resumen Semanal</h4>
        <div className="hdp-week-grid">
          {dayOfWeekOrder.map((day) => {
            const daySummary = summary.find(s => s.dayOfWeek === day);
            const isComplete = daySummary?.isComplete || false;
            const percentage = daySummary?.totalPercentage || 0;

            return (
              <div
                key={day}
                className={`hdp-week-card ${isComplete ? 'complete' : ''} ${day === selectedDay ? 'selected' : ''}`}
                onClick={() => setSelectedDay(day)}
              >
                <div className="hdp-week-name">{dayOfWeekShortLabels[day]}</div>
                <div className="hdp-week-pct">{percentage.toFixed(0)}%</div>
                <div className="hdp-week-bar">
                  <div className="hdp-week-fill" style={{ width: `${Math.min(percentage, 100)}%` }} />
                </div>
                <div className="hdp-week-count">{daySummary?.activeDistributions || 0}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Distribute Modal */}
      {showDistributeModal && (
        <div className="hdp-modal-overlay" onClick={() => setShowDistributeModal(false)}>
          <div className="hdp-modal" onClick={(e) => e.stopPropagation()}>
            <div className="hdp-modal-header">
              <h3><ZapIcon /> Distribuir Uniformemente - {dayOfWeekLabels[selectedDay]}</h3>
              <button className="hdp-modal-close" onClick={() => setShowDistributeModal(false)}><CloseIcon /></button>
            </div>
            <div className="hdp-modal-body">
              <div className="hdp-info-box">
                <InfoIcon />
                <p>Crea distribuciones automáticas por hora, repartiendo el 100% uniformemente.</p>
              </div>
              <div className="hdp-form-section">
                <label><ClockIcon /> Horario Laboral</label>
                <div className="hdp-time-row">
                  <select value={distributeConfig.businessStartHour} onChange={(e) => setDistributeConfig({ ...distributeConfig, businessStartHour: parseInt(e.target.value) })}>
                    {Array.from({ length: 24 }, (_, i) => <option key={i} value={i}>{formatTime(i, 0)}</option>)}
                  </select>
                  <span>a</span>
                  <select value={distributeConfig.businessEndHour} onChange={(e) => setDistributeConfig({ ...distributeConfig, businessEndHour: parseInt(e.target.value) })}>
                    {Array.from({ length: 24 }, (_, i) => <option key={i} value={i}>{formatTime(i, 0)}</option>)}
                  </select>
                </div>
                <p className="hdp-help">{distributeConfig.businessEndHour - distributeConfig.businessStartHour} horas = {(100 / (distributeConfig.businessEndHour - distributeConfig.businessStartHour)).toFixed(2)}% c/u</p>
              </div>
              <label className="hdp-checkbox">
                <input type="checkbox" checked={distributeConfig.clearExisting} onChange={(e) => setDistributeConfig({ ...distributeConfig, clearExisting: e.target.checked })} />
                Eliminar distribuciones existentes
              </label>
            </div>
            <div className="hdp-modal-footer">
              <button className="hdp-btn hdp-btn-secondary" onClick={() => setShowDistributeModal(false)} disabled={distributing}>Cancelar</button>
              <button className="hdp-btn hdp-btn-primary" onClick={handleDistributeUniformly} disabled={distributing}>
                <ZapIcon /> {distributing ? 'Distribuyendo...' : 'Distribuir'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Copy Modal */}
      {showCopyModal && (
        <div className="hdp-modal-overlay" onClick={() => setShowCopyModal(false)}>
          <div className="hdp-modal" onClick={(e) => e.stopPropagation()}>
            <div className="hdp-modal-header">
              <h3><CopyIcon /> Copiar desde {dayOfWeekLabels[selectedDay]}</h3>
              <button className="hdp-modal-close" onClick={() => setShowCopyModal(false)}><CloseIcon /></button>
            </div>
            <div className="hdp-modal-body">
              <div className="hdp-info-box">
                <InfoIcon />
                <p>{distributions.length} distribuciones se copiarán a los días seleccionados.</p>
              </div>
              <div className="hdp-form-section">
                <div className="hdp-copy-actions">
                  <button className="hdp-link-btn" onClick={selectAllDaysForCopy}>Todos</button>
                  <button className="hdp-link-btn" onClick={clearCopySelection}>Ninguno</button>
                </div>
                <div className="hdp-days-grid">
                  {dayOfWeekOrder.filter(d => d !== selectedDay).map((day) => {
                    const daySummary = summary.find(s => s.dayOfWeek === day);
                    const isSelected = copyConfig.toDays.includes(day);
                    return (
                      <label key={day} className={`hdp-day-check ${isSelected ? 'selected' : ''}`}>
                        <input type="checkbox" checked={isSelected} onChange={() => toggleCopyDay(day)} />
                        <span>{dayOfWeekShortLabels[day]}</span>
                        <small>{daySummary?.totalPercentage.toFixed(0) || 0}%</small>
                      </label>
                    );
                  })}
                </div>
              </div>
              <label className="hdp-checkbox hdp-checkbox-warn">
                <input type="checkbox" checked={copyConfig.overwrite} onChange={(e) => setCopyConfig({ ...copyConfig, overwrite: e.target.checked })} />
                Sobrescribir existentes
              </label>
            </div>
            <div className="hdp-modal-footer">
              <button className="hdp-btn hdp-btn-secondary" onClick={() => { setShowCopyModal(false); setCopyConfig({ toDays: [], overwrite: false }); }} disabled={copying}>Cancelar</button>
              <button className="hdp-btn hdp-btn-primary" onClick={handleCopyDistributions} disabled={copying || copyConfig.toDays.length === 0}>
                <CopyIcon /> {copying ? 'Copiando...' : `Copiar (${copyConfig.toDays.length})`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {(showCreateModal || editingDistribution) && (
        <div className="hdp-modal-overlay" onClick={closeModal}>
          <div className="hdp-modal hdp-modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="hdp-modal-header">
              <h3>{editingDistribution ? <><EditIcon /> Editar</> : <><PlusIcon /> Nueva - {dayOfWeekLabels[selectedDay]}</>}</h3>
              <button className="hdp-modal-close" onClick={closeModal}><CloseIcon /></button>
            </div>
            <div className="hdp-modal-body">
              <div className="hdp-form-section">
                <label><ClockIcon /> Rango Horario</label>
                <div className="hdp-time-grid">
                  <div className="hdp-time-group">
                    <span>Inicio</span>
                    <div className="hdp-time-inputs">
                      <select value={formData.startHour} onChange={(e) => setFormData({ ...formData, startHour: parseInt(e.target.value) })}>
                        {Array.from({ length: 24 }, (_, i) => <option key={i} value={i}>{formatTime(i, 0)}</option>)}
                      </select>
                      <select value={formData.startMinute} onChange={(e) => setFormData({ ...formData, startMinute: parseInt(e.target.value) })}>
                        {[0, 15, 30, 45].map((m) => <option key={m} value={m}>{m.toString().padStart(2, '0')}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="hdp-time-group">
                    <span>Fin</span>
                    <div className="hdp-time-inputs">
                      <select value={formData.endHour} onChange={(e) => setFormData({ ...formData, endHour: parseInt(e.target.value) })}>
                        {Array.from({ length: 24 }, (_, i) => <option key={i} value={i}>{formatTime(i, 0)}</option>)}
                      </select>
                      <select value={formData.endMinute} onChange={(e) => setFormData({ ...formData, endMinute: parseInt(e.target.value) })}>
                        {[0, 15, 30, 45].map((m) => <option key={m} value={m}>{m.toString().padStart(2, '0')}</option>)}
                      </select>
                    </div>
                  </div>
                </div>
              </div>
              <div className="hdp-form-section">
                <label><ChartIcon /> Porcentaje</label>
                <div className="hdp-pct-input">
                  <input type="number" min="0" max="100" step="0.1" value={formData.percentage} onChange={(e) => setFormData({ ...formData, percentage: parseFloat(e.target.value) })} />
                  <span>%</span>
                </div>
                <p className="hdp-help">Actual: {currentDaySummary?.totalPercentage.toFixed(1) || 0}% asignado</p>
              </div>
              <div className="hdp-form-section">
                <label>Descripción (opcional)</label>
                <input type="text" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Ej: Hora pico almuerzo" className="hdp-input" />
              </div>
            </div>
            <div className="hdp-modal-footer">
              <button className="hdp-btn hdp-btn-secondary" onClick={closeModal} disabled={creating || updating}>Cancelar</button>
              <button className="hdp-btn hdp-btn-primary" onClick={editingDistribution ? handleUpdate : handleCreate} disabled={creating || updating}>
                <SaveIcon /> {creating || updating ? 'Guardando...' : editingDistribution ? 'Actualizar' : 'Crear'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{styles}</style>
    </div>
  );
};

const styles = `
  .hdp-panel {
    background: #fff;
    border-radius: 6px;
    border: 1px solid #dbeafe;
  }

  .hdp-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 16px;
    border-bottom: 1px solid #dbeafe;
    background: #f8fafc;
    border-radius: 6px 6px 0 0;
  }

  .hdp-header-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: 600;
    font-size: 0.85rem;
    color: #1e40af;
  }

  .hdp-days-bar {
    display: flex;
    gap: 6px;
    padding: 12px;
    background: #f8fafc;
    overflow-x: auto;
  }

  .hdp-day-tab {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 8px 12px;
    border: 1px solid #dbeafe;
    border-radius: 6px;
    background: #fff;
    cursor: pointer;
    min-width: 60px;
    position: relative;
    transition: all 0.15s;
  }

  .hdp-day-tab:hover { border-color: #3b82f6; }
  .hdp-day-tab.selected {
    background: #2563eb;
    border-color: #2563eb;
    color: #fff;
  }
  .hdp-day-tab.selected .hdp-day-dot { background: #93c5fd; }

  .hdp-day-name { font-weight: 600; font-size: 0.75rem; }
  .hdp-day-pct { font-size: 0.65rem; opacity: 0.8; }

  .hdp-day-dot {
    position: absolute;
    top: -4px;
    right: -4px;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: #fbbf24;
    border: 2px solid #fff;
  }
  .hdp-day-dot.complete { background: #3b82f6; }

  .hdp-day-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 16px;
    background: #f8fafc;
    border-bottom: 1px solid #dbeafe;
    flex-wrap: wrap;
    gap: 12px;
  }

  .hdp-day-info h3 {
    margin: 0 0 4px 0;
    font-size: 0.9rem;
    color: #1e3a8a;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .hdp-day-stats {
    display: flex;
    gap: 12px;
    font-size: 0.7rem;
  }

  .hdp-day-stats span {
    padding: 2px 8px;
    border-radius: 4px;
    background: #dbeafe;
    color: #1e40af;
  }

  .hdp-day-stats .complete { background: #3b82f6; color: #fff; }
  .hdp-day-stats .pending { background: #fef3c7; color: #92400e; }

  .hdp-day-actions {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
  }

  .hdp-alert {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 10px 16px;
    background: #fef3c7;
    border-left: 3px solid #f59e0b;
    margin: 12px 16px;
    border-radius: 4px;
    font-size: 0.75rem;
    color: #92400e;
  }

  .hdp-alert strong { display: block; margin-bottom: 2px; }
  .hdp-alert p { margin: 0; }

  .hdp-progress-section {
    padding: 12px 16px;
    border-bottom: 1px solid #dbeafe;
  }

  .hdp-progress-header {
    display: flex;
    justify-content: space-between;
    font-size: 0.7rem;
    color: #64748b;
    margin-bottom: 6px;
  }

  .hdp-progress-header .complete { color: #2563eb; font-weight: 600; }

  .hdp-progress-bar {
    height: 8px;
    background: #e2e8f0;
    border-radius: 4px;
    overflow: hidden;
  }

  .hdp-progress-fill {
    height: 100%;
    background: #3b82f6;
    transition: width 0.3s;
  }

  .hdp-progress-fill.complete { background: #2563eb; }
  .hdp-progress-fill.over { background: #ef4444; }

  .hdp-table-section { padding: 12px 16px; }

  .hdp-loading, .hdp-loading-inline {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 32px;
    color: #64748b;
    font-size: 0.8rem;
  }

  .hdp-spinner, .hdp-spinner-sm {
    border: 2px solid #e2e8f0;
    border-top-color: #3b82f6;
    border-radius: 50%;
    animation: hdp-spin 0.6s linear infinite;
  }

  .hdp-spinner { width: 24px; height: 24px; }
  .hdp-spinner-sm { width: 16px; height: 16px; }

  @keyframes hdp-spin { to { transform: rotate(360deg); } }

  .hdp-empty {
    text-align: center;
    padding: 32px;
    color: #64748b;
  }

  .hdp-empty svg { margin-bottom: 8px; opacity: 0.5; }
  .hdp-empty p { margin: 0 0 12px 0; font-size: 0.8rem; }

  .hdp-empty-actions {
    display: flex;
    gap: 8px;
    justify-content: center;
  }

  .hdp-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.75rem;
  }

  .hdp-table th, .hdp-table td {
    padding: 8px 10px;
    text-align: left;
    border-bottom: 1px solid #e2e8f0;
  }

  .hdp-table th {
    background: #f8fafc;
    font-weight: 600;
    color: #64748b;
    font-size: 0.65rem;
    text-transform: uppercase;
  }

  .hdp-table tr:hover { background: #f8fafc; }
  .hdp-table tr.inactive { opacity: 0.5; }

  .hdp-time-cell { font-weight: 600; color: #1e3a8a; white-space: nowrap; }

  .hdp-pct-badge {
    display: inline-block;
    padding: 2px 8px;
    background: #dbeafe;
    color: #1e40af;
    border-radius: 4px;
    font-weight: 600;
  }

  .hdp-muted { color: #94a3b8; }

  .hdp-status-btn {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 8px;
    border: 1px solid #e2e8f0;
    border-radius: 4px;
    background: #f1f5f9;
    color: #64748b;
    font-size: 0.65rem;
    cursor: pointer;
    transition: all 0.15s;
  }

  .hdp-status-btn.active {
    background: #dbeafe;
    border-color: #3b82f6;
    color: #1e40af;
  }

  .hdp-action-btns { display: flex; gap: 4px; }

  .hdp-weekly {
    padding: 16px;
    background: #f8fafc;
    border-top: 1px solid #dbeafe;
  }

  .hdp-weekly h4 {
    margin: 0 0 12px 0;
    font-size: 0.8rem;
    color: #1e40af;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .hdp-week-grid {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 8px;
  }

  @media (max-width: 800px) { .hdp-week-grid { grid-template-columns: repeat(4, 1fr); } }
  @media (max-width: 500px) { .hdp-week-grid { grid-template-columns: repeat(2, 1fr); } }

  .hdp-week-card {
    padding: 10px;
    background: #fff;
    border: 1px solid #dbeafe;
    border-radius: 6px;
    text-align: center;
    cursor: pointer;
    transition: all 0.15s;
  }

  .hdp-week-card:hover { border-color: #3b82f6; }
  .hdp-week-card.selected { border-color: #2563eb; box-shadow: 0 0 0 2px #dbeafe; }
  .hdp-week-card.complete { border-left: 3px solid #3b82f6; }

  .hdp-week-name { font-size: 0.7rem; font-weight: 600; color: #64748b; }
  .hdp-week-pct { font-size: 1rem; font-weight: 700; color: #1e3a8a; margin: 4px 0; }

  .hdp-week-bar {
    height: 4px;
    background: #e2e8f0;
    border-radius: 2px;
    margin-bottom: 4px;
    overflow: hidden;
  }

  .hdp-week-fill { height: 100%; background: #3b82f6; }
  .hdp-week-card.complete .hdp-week-fill { background: #2563eb; }

  .hdp-week-count { font-size: 0.6rem; color: #94a3b8; }

  /* Buttons */
  .hdp-btn {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 6px 12px;
    border: none;
    border-radius: 4px;
    font-size: 0.7rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.15s;
  }

  .hdp-btn:disabled { opacity: 0.5; cursor: not-allowed; }

  .hdp-btn-primary { background: #2563eb; color: #fff; }
  .hdp-btn-primary:hover:not(:disabled) { background: #1e40af; }

  .hdp-btn-secondary { background: #e2e8f0; color: #475569; }
  .hdp-btn-secondary:hover:not(:disabled) { background: #cbd5e1; }

  .hdp-btn-danger { background: #fef2f2; color: #b91c1c; }
  .hdp-btn-danger:hover:not(:disabled) { background: #fee2e2; }

  .hdp-btn-sm { padding: 4px 8px; font-size: 0.65rem; }
  .hdp-btn-icon { padding: 6px; }

  .hdp-link-btn {
    background: none;
    border: none;
    color: #2563eb;
    font-size: 0.7rem;
    cursor: pointer;
    padding: 0;
  }

  .hdp-link-btn:hover { text-decoration: underline; }

  /* Modal */
  .hdp-modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.4);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 16px;
  }

  .hdp-modal {
    background: #fff;
    border-radius: 8px;
    width: 100%;
    max-width: 420px;
    max-height: 90vh;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  .hdp-modal-lg { max-width: 500px; }

  .hdp-modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 16px;
    border-bottom: 1px solid #dbeafe;
    background: #f8fafc;
  }

  .hdp-modal-header h3 {
    margin: 0;
    font-size: 0.85rem;
    color: #1e3a8a;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .hdp-modal-close {
    background: none;
    border: none;
    color: #64748b;
    cursor: pointer;
    padding: 4px;
    display: flex;
  }

  .hdp-modal-close:hover { color: #1e3a8a; }

  .hdp-modal-body {
    padding: 16px;
    overflow-y: auto;
  }

  .hdp-modal-footer {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding: 12px 16px;
    border-top: 1px solid #dbeafe;
    background: #f8fafc;
  }

  .hdp-info-box {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 10px;
    background: #eff6ff;
    border-radius: 4px;
    margin-bottom: 16px;
    font-size: 0.75rem;
    color: #1e40af;
  }

  .hdp-info-box p { margin: 0; }

  .hdp-form-section {
    margin-bottom: 16px;
  }

  .hdp-form-section > label {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 0.7rem;
    font-weight: 600;
    color: #475569;
    margin-bottom: 8px;
  }

  .hdp-time-row {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .hdp-time-row select {
    flex: 1;
    padding: 6px 8px;
    border: 1px solid #dbeafe;
    border-radius: 4px;
    font-size: 0.75rem;
  }

  .hdp-time-row span { color: #64748b; font-size: 0.75rem; }

  .hdp-time-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }

  .hdp-time-group span {
    display: block;
    font-size: 0.65rem;
    color: #64748b;
    margin-bottom: 4px;
  }

  .hdp-time-inputs {
    display: flex;
    gap: 4px;
  }

  .hdp-time-inputs select {
    flex: 1;
    padding: 6px;
    border: 1px solid #dbeafe;
    border-radius: 4px;
    font-size: 0.75rem;
  }

  .hdp-help {
    font-size: 0.65rem;
    color: #64748b;
    margin: 6px 0 0 0;
  }

  .hdp-checkbox {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 0.75rem;
    color: #475569;
    cursor: pointer;
    padding: 8px;
    border-radius: 4px;
  }

  .hdp-checkbox:hover { background: #f8fafc; }
  .hdp-checkbox-warn { background: #fef3c7; }

  .hdp-checkbox input { width: 16px; height: 16px; }

  .hdp-copy-actions {
    display: flex;
    gap: 12px;
    margin-bottom: 10px;
  }

  .hdp-days-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
  }

  .hdp-day-check {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 10px;
    border: 1px solid #dbeafe;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.75rem;
    transition: all 0.15s;
  }

  .hdp-day-check:hover { border-color: #3b82f6; }
  .hdp-day-check.selected { background: #eff6ff; border-color: #3b82f6; }
  .hdp-day-check input { width: 14px; height: 14px; }
  .hdp-day-check span { font-weight: 500; }
  .hdp-day-check small { margin-left: auto; color: #64748b; }

  .hdp-pct-input {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .hdp-pct-input input {
    width: 100px;
    padding: 6px 10px;
    border: 1px solid #dbeafe;
    border-radius: 4px;
    font-size: 0.85rem;
    text-align: right;
  }

  .hdp-pct-input span { color: #64748b; font-weight: 600; }

  .hdp-input {
    width: 100%;
    padding: 8px 10px;
    border: 1px solid #dbeafe;
    border-radius: 4px;
    font-size: 0.75rem;
  }

  .hdp-input:focus { outline: none; border-color: #3b82f6; }
`;
